/* sugar:assets/silent-subscription.js@2.0.0 */
/*
 * silent-subscription — one selling plan on the product form, with nothing to pick.
 *
 * A root is any [data-silent-subscription]; its data-silent-subscription-config carries the plan
 * the block resolved in Liquid and the selected variant's prices. The root seeds the section's
 * subscription state (what the price store, the offers block, the sticky bar and the buy button
 * read), keeps the form's selling_plan input set, and announces the plan the way a Subscriptions
 * block would, so every price on the page shows the plan's price and the cart line carries it.
 *
 * With a Subscriptions or Subscription Toggle block in the same section, this root stays quiet:
 * the shopper's choice wins.
 */
(function () {
  'use strict';
  if (window.SugarSilentSubscription) return;

  const ROOT = '[data-silent-subscription]';

  class SilentSubscription {
    constructor(root) {
      this.root = root;
      root.sugarSilentSubscription = this;
      try {
        this.cfg = JSON.parse(root.dataset.silentSubscriptionConfig || '{}');
      } catch (e) {
        this.cfg = {};
      }
      this.sectionId = String(this.cfg.sectionId || '');
      // The plan the block names, and the plans of variants that can't take it (each pack size
      // can have its own group with a plan of the same name): the plan follows the variant.
      this.basePlan = this.cfg.plan || null;
      this.plansById = {};
      (this.cfg.plans || []).forEach((plan) => { this.plansById[String(plan.id)] = plan; });
      if (this.basePlan) this.plansById[String(this.basePlan.id)] = this.basePlan;
      this.variantPlans = this.cfg.variantPlans || {};
      this.plan = this.planForVariant(this.cfg.variantId);
      // What the struck-through prices compare the plan's price with (the block's Compare price shows)
      this.compareBasis = this.cfg.compareBasis || 'onetime';
      this.handlers = [];
      if (this.plan && this.sectionId) this.init();
    }

    init() {
      this.state = {
        type: 'subscribe',
        sellingPlan: this.plan,
        sellingPlanId: this.plan.id,
        sellingPlanGroupName: this.plan.group_name,
        compareBasis: this.compareBasis,
        variantPrice: this.cfg.variantPrice || 0,
        variantComparePrice: this.cfg.variantComparePrice || 0,
        quantity: 1,
      };
      this.listen(document, 'variant:change', (e) => {
        const detail = e.detail || {};
        if (detail.sectionId && String(detail.sectionId) !== this.sectionId) return;
        if (!detail.variant) return;
        this.state.variantPrice = detail.variant.price;
        this.state.variantComparePrice = detail.variant.compare_at_price || 0;
        this.usePlan(this.planForVariant(detail.variant.id));
        this.apply();
      });
      // A tier that locks a plan of its own (Offers) puts that plan on the form
      this.listen(document, 'offers:lock-selling-plan', (e) => {
        const detail = e.detail || {};
        if (detail.sectionId && String(detail.sectionId) !== this.sectionId) return;
        const plan = this.plansById[String(detail.sellingPlanId)];
        if (!plan || plan === this.plan) return;
        this.usePlan(plan);
        this.apply();
      });
      this.apply();
    }

    planForVariant(variantId) {
      const id = variantId != null ? this.variantPlans[String(variantId)] : null;
      return (id != null && this.plansById[String(id)]) || this.basePlan;
    }

    usePlan(plan) {
      if (!plan) return;
      this.plan = plan;
      this.state.sellingPlan = plan;
      this.state.sellingPlanId = plan.id;
      this.state.sellingPlanGroupName = plan.group_name;
    }

    destroy() {
      this.handlers.forEach(([target, type, fn]) => target.removeEventListener(type, fn));
      this.handlers = [];
      delete this.root.sugarSilentSubscription;
    }

    listen(target, type, fn) {
      target.addEventListener(type, fn);
      this.handlers.push([target, type, fn]);
    }

    sectionEl() {
      return document.getElementById('shopify-section-' + this.sectionId) || this.root.closest('.shopify-section');
    }

    // A picker in the section owns the choice
    yields() {
      const section = this.sectionEl();
      return !!(section && section.querySelector('[data-sugar-subscriptions]'));
    }

    apply() {
      if (this.yields()) return;
      window.subscriptionStateBySection = window.subscriptionStateBySection || {};
      window.subscriptionStateBySection[this.sectionId] = this.state;
      window.subscriptionState = this.state;
      this.updateForm();
      document.dispatchEvent(
        new CustomEvent('subscription:type-changed', {
          detail: {
            sectionId: this.sectionId,
            type: 'subscribe',
            sellingPlan: this.plan,
            sellingPlanId: this.plan.id,
            sellingPlanGroupName: this.plan.group_name,
            variantPrice: this.state.variantPrice,
            variantComparePrice: this.state.variantComparePrice,
            quantity: 1,
            compareBasis: this.compareBasis,
          },
        })
      );
    }

    updateForm() {
      const section = this.sectionEl();
      if (!section) return;
      const form =
        section.querySelector('form[data-type="add-to-cart-form"]') ||
        section.querySelector('product-form form') ||
        section.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      let input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }
      input.value = this.plan.id;
      input.disabled = false;
    }
  }

  /* ------------------------------------------------------------ boot */

  function init(scope) {
    const host = scope && scope.nodeType === 1 ? scope : document;
    const roots = host.matches && host.matches(ROOT) ? [host] : Array.from(host.querySelectorAll(ROOT));
    roots.forEach((root) => {
      if (!root.sugarSilentSubscription) new SilentSubscription(root);
    });
  }

  function teardown(scope) {
    const host = scope && scope.nodeType === 1 ? scope : null;
    if (!host) return;
    const roots = host.matches(ROOT) ? [host] : Array.from(host.querySelectorAll(ROOT));
    roots.forEach((root) => root.sugarSilentSubscription && root.sugarSilentSubscription.destroy());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // A section the editor re-renders arrives with a fresh root; the old one is let go
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        if (node.nodeType === 1) teardown(node);
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (node.matches(ROOT) || node.querySelector(ROOT))) init(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.SugarSilentSubscription = { init };
})();
